import { useEffect, useState } from 'react';
import { CollapsibleCard } from './CollapsibleCard';

const SLOT_PLACEHOLDERS = ['Something small...', 'Someone who helped...', 'A moment that landed right...'] as const;

const INPUT_STYLE = {
  width: '100%',
  minHeight: 'var(--touch-target-min)',
  padding: '0 var(--space-3)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-subtle)',
  background: 'var(--surface-sunken)',
  color: 'var(--text-primary)',
  fontSize: 'var(--type-body-size)',
  fontFamily: 'var(--font-family)',
} as const;

/**
 * Phone Gratitude (R8.11, today-phone-parity.md §4.4). Exactly three
 * numbered rows, independently optional/persisted, identical to desktop —
 * no further phone-specific compression needed beyond the collapse itself.
 */
export function GratitudeCardPhone({
  gratitude,
  onSetSlot,
  isLast,
}: {
  readonly gratitude: readonly [string, string, string];
  readonly onSetSlot: (slot: 0 | 1 | 2, text: string) => void;
  readonly isLast: boolean;
}) {
  const filled = gratitude.filter((g) => g.trim().length > 0).length;
  // ux-designer audit flagged this summary as missing the "Gratitude · "
  // prefix from today-phone-parity.md §4.1's copy table. Deliberately NOT
  // added: CollapsibleCard renders `title` and `summary` as two separate
  // always-visible text spans with no aria-label, so their text
  // concatenates into one accessible name -- prefixing the summary would
  // make both the visible text AND the announced name repeat "Gratitude"
  // twice back-to-back ("Gratitude Gratitude · 1 of 3 filled"). Spec
  // updated to match (today-phone-parity.md §4.1 changelog) rather than
  // introducing that duplication.
  const summary = filled > 0 ? `${filled} of 3 filled` : undefined;

  return (
    <CollapsibleCard title="Gratitude" summary={summary} isLast={isLast}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {([0, 1, 2] as const).map((slot) => (
          <GratitudeSlotRow
            key={slot}
            slot={slot}
            value={gratitude[slot]}
            placeholder={SLOT_PLACEHOLDERS[slot]}
            onCommit={(text) => onSetSlot(slot, text)}
          />
        ))}
      </div>
    </CollapsibleCard>
  );
}

function GratitudeSlotRow({
  slot,
  value,
  placeholder,
  onCommit,
}: {
  readonly slot: 0 | 1 | 2;
  readonly value: string;
  readonly placeholder: string;
  readonly onCommit: (text: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
      <span
        className="num"
        style={{ flex: 'none', width: '20px', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}
      >
        {String(slot + 1).padStart(2, '0')}
      </span>
      <input
        aria-label={`Gratitude slot ${slot + 1}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== value) onCommit(draft);
        }}
        placeholder={placeholder}
        style={INPUT_STYLE}
      />
    </div>
  );
}
